import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Navigation from '../components/Navigation';
import { Card, CardHeader, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calendar, TrendingUp, Users } from 'lucide-react';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Blog = ({ user, setUser }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [userLoading, setUserLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPosts();
    // Allow time for user state to settle
    const timer = setTimeout(() => setUserLoading(false), 100);
    return () => clearTimeout(timer);
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog-posts`);
      setPosts(response.data.posts);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const generatePost = async () => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/generate-blog-post`);
      toast.success('Blog post generated successfully! 🎉');
      fetchPosts(); // Refresh the list
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate blog post');
      console.error('Error generating post:', error);
    } finally {
      setGenerating(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <>
      <SEO 
        title="Blog - TV Show Insights & Community Trends | baad.tv"
        description="Discover trending shows, community sentiment, and top-rated TV content from the baad.tv flock. AI-powered insights from real viewer reviews."
        url="/blog"
      />
      <div className="blog-container">
        {!userLoading && user && <Navigation user={user} setUser={setUser} currentPage="blog" />}
        {!userLoading && !user && (
          <div className="public-header">
            <div className="header-content">
              <h1 onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>baad.tv</h1>
              <Button onClick={() => navigate('/')} style={{ backgroundColor: '#E62F88' }}>
                Sign In
              </Button>
            </div>
          </div>
        )}
        
        <div className="blog-content">
          <div className="page-header">
            <h1>The Flock's Blog</h1>
            <p>AI-powered insights from your community's TV reviews 🐑</p>
          </div>

          {user?.email === 'vmurgatroyd@gmail.com' && (
            <Card className="admin-card">
              <CardContent style={{ padding: '1.5rem' }}>
                <div className="admin-controls">
                  <p style={{ marginBottom: '1rem', color: '#b0b0b0' }}>
                    <strong>Admin:</strong> Generate a new blog post analyzing recent reviews
                  </p>
                  <Button 
                    onClick={generatePost}
                    disabled={generating}
                    style={{ backgroundColor: '#E62F88' }}
                  >
                    {generating ? 'Generating...' : '🤖 Generate Blog Post'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {loading ? (
            <div className="loading-state">
              <p>Loading blog posts...</p>
            </div>
          ) : posts.length === 0 ? (
            <Card>
              <CardContent style={{ padding: '3rem', textAlign: 'center' }}>
                <TrendingUp size={48} style={{ color: '#E62F88', marginBottom: '1rem' }} />
                <h3>No blog posts yet!</h3>
                <p style={{ color: '#b0b0b0', marginTop: '0.5rem' }}>
                  Check back soon for AI-generated insights about trending shows and community sentiment.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="posts-grid">
              {posts.map((post) => (
                <Card key={post.id} className="post-card">
                  <CardHeader>
                    <h2>{post.title}</h2>
                    <div className="post-meta">
                      <span className="meta-item">
                        <Calendar size={16} />
                        {formatDate(post.created_at)}
                      </span>
                      <span className="meta-item">
                        <Users size={16} />
                        {post.stats.total_reviews} reviews
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="post-excerpt">{post.excerpt}</p>
                    <div className="top-shows">
                      <strong>Featured Shows:</strong>
                      <div className="show-badges">
                        {post.stats.top_shows.map((show, idx) => (
                          <span key={idx} className="show-badge">{show}</span>
                        ))}
                      </div>
                    </div>
                    <Button 
                      onClick={() => navigate(`/blog/${post.slug}`)}
                      variant="outline"
                      style={{ marginTop: '1rem' }}
                    >
                      Read Full Post →
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        <style jsx>{`
          .blog-container {
            min-height: 100vh;
            background: #121212;
          }

          .public-header {
            background: #1a1a1a;
            border-bottom: 1px solid #333333;
            padding: 1rem 2rem;
          }

          .header-content {
            max-width: 1200px;
            margin: 0 auto;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          .public-header h1 {
            color: #E62F88;
            font-size: 1.75rem;
            font-weight: 700;
          }

          .blog-content {
            max-width: 1200px;
            margin: 0 auto;
            padding: 2rem;
          }

          .page-header {
            margin-bottom: 2.5rem;
          }

          .page-header h1 {
            font-size: 2.5rem;
            font-weight: 700;
            color: #ffffff;
            margin-bottom: 0.5rem;
          }

          .page-header p {
            color: #b0b0b0;
            font-size: 1.1rem;
          }

          .admin-card {
            background: #1a1a1a;
            border: 1px solid #E62F88;
            margin-bottom: 2rem;
          }

          .loading-state {
            text-align: center;
            padding: 3rem;
            color: #b0b0b0;
          }

          .posts-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
            gap: 2rem;
          }

          .post-card {
            background: #1a1a1a;
            border: 1px solid #333333;
            transition: transform 0.2s, border-color 0.2s;
          }

          .post-card:hover {
            transform: translateY(-4px);
            border-color: #E62F88;
          }

          .post-card h2 {
            font-size: 1.5rem;
            color: #ffffff;
            margin-bottom: 1rem;
          }

          .post-meta {
            display: flex;
            gap: 1.5rem;
            color: #b0b0b0;
            font-size: 0.9rem;
          }

          .meta-item {
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }

          .post-excerpt {
            color: #b0b0b0;
            line-height: 1.6;
            margin-bottom: 1rem;
          }

          .top-shows {
            padding: 1rem;
            background: #2a2a2a;
            border-radius: 8px;
            border: 1px solid #333333;
          }

          .top-shows strong {
            color: #E62F88;
            display: block;
            margin-bottom: 0.75rem;
          }

          .show-badges {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
          }

          .show-badge {
            background: #E62F88;
            color: #000000;
            padding: 0.25rem 0.75rem;
            border-radius: 20px;
            font-size: 0.85rem;
            font-weight: 600;
          }

          @media (max-width: 768px) {
            .blog-content {
              padding: 1rem;
            }

            .page-header h1 {
              font-size: 1.75rem;
            }

            .posts-grid {
              grid-template-columns: 1fr;
              gap: 1.5rem;
            }
          }
        `}</style>
      </div>
    </>
  );
};

export default Blog;
